import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import type { ScenarioFormValues } from '../../model/scenario-form';
import type { ToolCatalogEntry } from '../../model/toolCatalog';
import { argChipsForTool } from '../../model/toolCatalog';

interface ScenarioToolTurnFieldsProps {
  index: number;
  tools: ToolCatalogEntry[];
}

const darkFieldClasses = 'h-8 border-white/70 bg-brand-vivid text-xs text-white';

const ScenarioToolTurnFields = ({ index, tools }: ScenarioToolTurnFieldsProps): JSX.Element => {
  const form = useFormContext<ScenarioFormValues>();
  const toolName = form.watch(`turns.${index}.toolName`) ?? '';
  const selectedTool = tools.find((tool) => tool.name === toolName) ?? null;
  const argChips = selectedTool ? argChipsForTool(selectedTool) : [];
  const seedCases = selectedTool?.seed?.cases ?? [];

  const applySeedCase = (caseKey: string): void => {
    const seedCase = seedCases.find((c) => c.key === caseKey);
    if (!seedCase) return;
    form.setValue(`turns.${index}.content`, JSON.stringify(seedCase.response, null, 2), { shouldDirty: true });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <FormField
          control={form.control}
          name={`turns.${index}.toolName`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger size="sm" className={`${darkFieldClasses} w-full`}>
                    <SelectValue placeholder="Tool…" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {tools.map((tool) => (
                    <SelectItem key={tool.name} value={tool.name}>
                      <span className="font-mono text-xs">{tool.name}</span>
                      {tool.availableInProject === false ? (
                        <span className="text-text-muted"> · no disponible en este proyecto</span>
                      ) : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
        <Select onValueChange={applySeedCase} disabled={seedCases.length === 0}>
          <SelectTrigger size="sm" className={`${darkFieldClasses} flex-1 disabled:opacity-60`}>
            <SelectValue placeholder={seedCases.length > 0 ? 'Responde con…' : 'Sin seed'} />
          </SelectTrigger>
          <SelectContent>
            {seedCases.map((seedCase) => (
              <SelectItem key={seedCase.key} value={seedCase.key}>
                {seedCase.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {argChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-white/80">Argumentos (referencia):</span>
          {argChips.map((chip) => (
            <span key={chip} className="rounded-md bg-white/15 px-2 py-0.5 font-mono text-[10px] text-white">
              {chip}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ScenarioToolTurnFields;
